import { useState } from "react";
import { Box, Button, Typography, CircularProgress } from "@mui/material";
import { FiPlus } from "react-icons/fi";
import ConfirmModal from "../../components/ui/ConfirmModal";
import AdminCategoryList from "./components/AdminCategoryList";
import AdminCategoryFormDialog from "./components/AdminCategoryFormDialog";
import {
  Categorie,
  categoriesService,
} from "../../apiServices/categories.service";
import { useAllCategories } from "./hooks/useAllCategories";
import { useTableState } from "./hooks/useTableState";
import TableFilters from "./components/TableFilter";
import MultiSelectFilter from "./components/MultiSelectFilter";
import { STATUS_OPTIONS } from "./constants/enums";

export default function AdminCategories() {
  const { categories, loading, error, refetch } = useAllCategories();

  const [openDialog, setOpenDialog] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Categorie | null>(
    null,
  );
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    category: Categorie | null;
  }>({ open: false, category: null });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // ─── Filtros y ordenamiento ────────────────────────────────────────────
  const {
    search,
    setSearch,
    filters,
    setSpecificFilter,
    resetFilters,
    hasActiveFilters,
    order,
    orderBy,
    handleRequestSort,
    processedData: filteredCategories,
  } = useTableState({
    data: categories,
    initialSortKey: "nombreEspanol",
    searchExtractor: (item) => item.nombreEspanol,
  });

  // ─── Handlers de CRUD ─────────────────────────────────────────────────

  const handleOpenDialog = (category: Categorie | null = null) => {
    setEditingCategory(category);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingCategory(null);
  };

  const handleOpenConfirmDialog = (category: Categorie) => {
    setConfirmDialog({ open: true, category });
  };

  const handleCloseConfirmDialog = () => {
    setConfirmDialog({ open: false, category: null });
  };

  const handleConfirmToggle = async () => {
    if (!confirmDialog.category) return;

    const { categoriaId, activo, nombreEspanol, nombreIngles } =
      confirmDialog.category;

    setIsSubmitting(true);
    try {
      if (activo) {
        // Si está activa, el endpoint DELETE (baja lógica) la pone en activo: false
        await categoriesService.delete(categoriaId);
      } else {
        // Si está inactiva, PUT con activo: true para revivirla
        await categoriesService.update(categoriaId, {
          nombreEspanol,
          nombreIngles,
          activo: true,
        });
      }
      await refetch();
    } catch (error) {
      console.error("Error al cambiar el estado de la categoría:", error);
    } finally {
      setIsSubmitting(false);
      handleCloseConfirmDialog();
    }
  };

  const handleSave = async (categoryData: {
    categoriaId?: number;
    nombreEspanol: string;
    nombreIngles: string;
  }) => {
    setIsSubmitting(true);
    try {
      const payload = {
        nombreEspanol: categoryData.nombreEspanol,
        nombreIngles: categoryData.nombreIngles,
      };

      if (categoryData.categoriaId) {
        await categoriesService.update(categoryData.categoriaId, payload);
      } else {
        await categoriesService.insert(payload);
      }
      await refetch();
    } catch (error) {
      console.error("Error al guardar la categoría:", error);
    } finally {
      setIsSubmitting(false);
      handleCloseDialog();
    }
  };

  if (loading && categories.length === 0)
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "40vh",
        }}
      >
        <CircularProgress size={52} />
      </Box>
    );
  if (error) return <Typography color="error">{error}</Typography>;

  return (
    <Box sx={{ width: "100%" }}>
      {/* HEADER */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "stretch", sm: "center" },
          gap: { xs: 2, sm: 0 },
          mb: 4,
        }}
      >
        <Typography
          variant="h5"
          component="h1"
          sx={{ fontWeight: 600, color: "text.primary" }}
        >
          Listado de Categorías
        </Typography>
        <Button
          variant="contained"
          startIcon={<FiPlus />}
          onClick={() => handleOpenDialog(null)}
          sx={{
            bgcolor: "primary.main",
            textTransform: "none",
            fontWeight: 600,
            borderRadius: "8px",
            padding: "8px 16px",
            "&:hover": { bgcolor: "primary.dark" },
          }}
        >
          Nueva Categoría
        </Button>
      </Box>

      {/* FILTROS */}
      {!loading && !error && categories.length > 0 && (
        <TableFilters
          search={search}
          onSearchChange={setSearch}
          onClear={resetFilters}
          hasActiveFilters={hasActiveFilters}
          searchPlaceholder="Buscar por nombre..."
        >
          <MultiSelectFilter
            label="Estado"
            value={filters["activo"] || []}
            options={STATUS_OPTIONS}
            onChange={(vals) => setSpecificFilter("activo", vals)}
          />
        </TableFilters>
      )}

      {/* CONTENIDO */}
      {filteredCategories.length === 0 && !loading && !error ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            height: "20vh",
            alignItems: "center",
          }}
        >
          <Typography color="text.secondary">
            No se encontraron registros coincidentes.
          </Typography>
        </Box>
      ) : (
        <AdminCategoryList
          categories={filteredCategories}
          onEdit={handleOpenDialog}
          onToggleStatus={handleOpenConfirmDialog}
          order={order}
          orderBy={String(orderBy)}
          onRequestSort={(key) => handleRequestSort(key as keyof Categorie)}
        />
      )}

      <AdminCategoryFormDialog
        open={openDialog}
        onClose={handleCloseDialog}
        onSave={handleSave}
        editingCategory={editingCategory}
        isSubmitting={isSubmitting}
      />

      <ConfirmModal
        open={confirmDialog.open}
        title="Confirmar Acción"
        content={
          <Typography>
            ¿Estás seguro de que deseas{" "}
            {confirmDialog.category?.activo ? "deshabilitar" : "habilitar"} la
            categoría <strong>"{confirmDialog.category?.nombreEspanol}"</strong>
            ?
          </Typography>
        }
        confirmText="Confirmar"
        cancelText="Cancelar"
        onConfirm={handleConfirmToggle}
        onCancel={handleCloseConfirmDialog}
        loading={isSubmitting}
      />
    </Box>
  );
}
